import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';

const BarInventory = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [inventory, setInventory] = useState([]);
  const [filter, setFilter] = useState('all'); // all, critical, low, good

  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444'
  };

  const isMobile = windowWidth <= 768;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = async () => {
    setLoading(true);
    try {
      const data = await apiGet('/admin/bar/inventory/items');
      if (data.success) setInventory(data.data);
    } catch (error) {
      console.error('Error fetching inventory:', error);
    }
    setLoading(false);
  };

  const getStockStatusColor = (status) => {
    const statusColors = {
      critical: colors.danger,
      low: colors.warning,
      good: colors.success
    };
    return statusColors[status] || colors.textLight;
  };

  const filteredInventory = inventory.filter(item => {
    if (filter === 'all') return true;
    return item.stock_status === filter;
  });

  const criticalCount = inventory.filter(i => i.stock_status === 'critical').length;
  const lowCount = inventory.filter(i => i.stock_status === 'low').length;

  const styles = {
    pageHeader: {
      marginBottom: isMobile ? '16px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
      gap: isMobile ? '12px' : '20px',
      marginBottom: isMobile ? '20px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    statCard: {
      background: colors.white,
      padding: isMobile ? '20px' : '24px',
      borderRadius: isMobile ? '14px' : '16px',
      boxShadow: '0 4px 12px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`
    },
    contentCard: {
      background: colors.white,
      padding: isMobile ? '16px' : '24px',
      borderRadius: isMobile ? '14px' : '16px',
      boxShadow: '0 8px 24px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`,
      margin: isMobile ? '0 4px' : '0'
    },
    filterContainer: {
      display: 'flex',
      gap: '8px',
      marginBottom: '16px',
      overflowX: 'auto'
    },
    filterButton: {
      padding: isMobile ? '8px 12px' : '10px 16px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      background: colors.white,
      fontSize: isMobile ? '12px' : '13px',
      fontWeight: '600',
      cursor: 'pointer',
      whiteSpace: 'nowrap'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: isMobile ? '12px' : '13px'
    },
    tableHeader: {
      background: colors.lightGray,
      fontWeight: '700',
      textAlign: 'left',
      padding: isMobile ? '10px' : '12px',
      borderBottom: `2px solid ${colors.mediumGray}`
    },
    tableCell: {
      padding: isMobile ? '10px' : '12px',
      borderBottom: `1px solid ${colors.lightGray}`
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <div>Loading inventory...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={styles.pageHeader}>
        <h1 style={{ fontSize: isMobile ? '20px' : '28px', fontWeight: '700', color: colors.textDark, marginBottom: '6px' }}>
          Bar Inventory
        </h1>
        <p style={{ fontSize: isMobile ? '12px' : '14px', color: colors.textLight }}>
          {isMobile ? 'Stock management' : 'Manage beverage stock levels and inventory tracking'}
        </p>
      </div>

      {/* Statistics */}
      <div style={styles.statsGrid}>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.primary}` }}>
          <div style={{ fontSize: isMobile ? '28px' : '36px', fontWeight: '800', color: colors.primary }}>
            {inventory.length}
          </div>
          <div style={{ fontSize: isMobile ? '11px' : '12px', color: colors.textLight, textTransform: 'uppercase', fontWeight: '600' }}>
            Total Items
          </div>
        </div>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.danger}` }}>
          <div style={{ fontSize: isMobile ? '28px' : '36px', fontWeight: '800', color: colors.danger }}>
            {criticalCount}
          </div>
          <div style={{ fontSize: isMobile ? '11px' : '12px', color: colors.textLight, textTransform: 'uppercase', fontWeight: '600' }}>
            Critical Stock
          </div>
        </div>
        <div style={{ ...styles.statCard, borderTop: `4px solid ${colors.warning}` }}>
          <div style={{ fontSize: isMobile ? '28px' : '36px', fontWeight: '800', color: colors.warning }}>
            {lowCount}
          </div>
          <div style={{ fontSize: isMobile ? '11px' : '12px', color: colors.textLight, textTransform: 'uppercase', fontWeight: '600' }}>
            Low Stock
          </div>
        </div>
      </div>

      {/* Inventory List */}
      <div style={styles.contentCard}>
        <h2 style={{ fontSize: isMobile ? '16px' : '20px', fontWeight: '700', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>📦</span> Beverage Inventory
        </h2>

        {/* Filters */}
        <div style={styles.filterContainer}>
          {['all', 'critical', 'low', 'good'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              style={{
                ...styles.filterButton,
                ...(filter === f ? { background: colors.primary, color: colors.white, borderColor: colors.primary } : {})
              }}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {filteredInventory.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
            No items found
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.tableHeader}>Item Name</th>
                  {!isMobile && <th style={styles.tableHeader}>Code</th>}
                  <th style={styles.tableHeader}>Stock</th>
                  <th style={styles.tableHeader}>Unit</th>
                  {!isMobile && <th style={styles.tableHeader}>Reorder</th>}
                  <th style={styles.tableHeader}>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredInventory.map(item => (
                  <tr key={item.id}>
                    <td style={styles.tableCell}>
                      <strong>{item.item_name}</strong>
                      {isMobile && item.used_in_drinks > 0 && (
                        <div style={{ fontSize: '11px', color: colors.textLight }}>
                          Used in {item.used_in_drinks} drink{item.used_in_drinks !== 1 ? 's' : ''}
                        </div>
                      )}
                    </td>
                    {!isMobile && (
                      <td style={styles.tableCell}>{item.item_code}</td>
                    )}
                    <td style={styles.tableCell}>
                      <strong>{parseFloat(item.current_stock).toFixed(2)}</strong>
                    </td>
                    <td style={styles.tableCell}>{item.unit}</td>
                    {!isMobile && (
                      <td style={styles.tableCell}>{parseFloat(item.reorder_point).toFixed(2)}</td>
                    )}
                    <td style={styles.tableCell}>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: '600',
                        background: `${getStockStatusColor(item.stock_status)}20`,
                        color: getStockStatusColor(item.stock_status)
                      }}>
                        {item.stock_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default BarInventory;